function normalizeSpecialDateTag(rawTag) {
  if (Array.isArray(rawTag)) {
    return rawTag.join(',');
  }

  if (typeof rawTag === 'string') {
    const trimmed = rawTag.trim();
    // Metaobject `tag` often serializes as a JSON-array string (e.g. ["טו באב"]).
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.join(',');
      }
    } catch (e) {
      // plain string tag
    }
    // Strip leftover JSON array syntax if present (mirrors Liquid); keep all tags.
    return trimmed
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .replace(/"/g, '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(',') || trimmed;
  }

  return '';
}

// Payload is static per page load — memoize against the script node so a missing
// early query does not poison the cache, and a replaced node re-parses.
let specialDatesMemo = { script: null, value: null };

function initSpecialDates() {
  try {
    const sectionScript = document.querySelector('script[data-special-dates]');
    if (!sectionScript) {
      return {};
    }

    if (specialDatesMemo.script === sectionScript) {
      return specialDatesMemo.value;
    }

    const specialDatesMetaobjects = {};
    const data = JSON.parse(sectionScript.textContent);
    if (!Array.isArray(data)) {
      specialDatesMemo = { script: sectionScript, value: {} };
      return specialDatesMemo.value;
    }

    data.forEach((item, index) => {
      const key = `special_date_${index}`;
      specialDatesMetaobjects[key] = {
        event: item.event || '',
        start_date: item.start_date || '',
        end_date: item.end_date || '',
        tag: normalizeSpecialDateTag(item.tag),
        collection_url: item.collection_url || ''
      };
    });
    specialDatesMemo = { script: sectionScript, value: specialDatesMetaobjects };
    return specialDatesMetaobjects;
  } catch (error) {
    console.error('Error initializing special dates:', error);
    return {};
  }
}

function checkSpecialCatalogDate(deliveryDate, specialDatesMetaobjects) {
  if (!deliveryDate) {
    return null;
  }

  const deliveryDateObj = parseDateOnly(deliveryDate);
  if (!deliveryDateObj) {
    return null;
  }

  for (const handle in specialDatesMetaobjects) {
    const specialDate = specialDatesMetaobjects[handle];

    if (!specialDate.start_date || !specialDate.end_date) {
      continue;
    }

    const startDate = parseDateOnly(specialDate.start_date);
    const endDate = parseDateOnly(specialDate.end_date);
    if (!startDate || !endDate) {
      continue;
    }

    if (deliveryDateObj >= startDate && deliveryDateObj <= endDate) {
      return {tag: specialDate.tag, event: specialDate.event, collectionUrl: specialDate.collection_url};
    }
  }

  return null;
}

function formatDateForDisplay(dateString) {
  if (!dateString) {
    return '';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString(document.documentElement.lang || 'he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
}

function showSpecialCatalogPopup(specialCatalog, deliveryDate) {
  
  const sessionStorageKey = 'special-catalog-popup-shown';
  if (sessionStorage.getItem(sessionStorageKey) === 'true') {
    return;
  }

  const popup = document.getElementById('special-catalog-popup');
  if (!popup) {
    console.warn('Special catalog popup element not found');
    return;
  }
  const formattedDate = formatDateForDisplay(deliveryDate);
  const messageElement = popup.querySelector('.special-catalog-modal__message');

  if (messageElement) {
    const messageTemplate = window.themeVariables?.strings?.specialCatalog?.popup?.message || '';
    messageElement.textContent = messageTemplate.replace('{{ date }}', formattedDate);
  }

  const catalogButton = popup.querySelector('#go-to-catalog-btn');
  if (!catalogButton) {
    console.warn('[Special Catalog] Button element not found!');
    return;
  }

  if (specialCatalog.collectionUrl) {
    const collectionUrl = extractCollectionUrl(specialCatalog.collectionUrl);
    if (collectionUrl) {
      catalogButton.setAttribute('data-catalog-url', collectionUrl);
      catalogButton.setAttribute('href', collectionUrl);
      catalogButton.style.display = '';
    } else {
      catalogButton.removeAttribute('data-catalog-url');
      catalogButton.style.display = 'none';
    }
  } else {
    catalogButton.removeAttribute('data-catalog-url');
    catalogButton.style.display = 'none';
  }

  if (typeof popup.open !== 'undefined') {
    popup.open = true;
  } else {
    popup.setAttribute('open', '');
  }
  
  sessionStorage.setItem(sessionStorageKey, 'true');

}

async function updateCartAttribute(attributeKeyOrObj, attributeValue) {
  const cartUpdateUrl = window.themeVariables?.routes?.cartUpdateUrl || (window.Shopify?.routes?.root || '') + 'cart/update.js';

  const attributes = typeof attributeKeyOrObj === 'object' ? attributeKeyOrObj : { [attributeKeyOrObj]: attributeValue };

  try {
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({attributes})
    });
    
    if (!response.ok) {
      throw new Error('Failed to update cart attributes');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating cart attribute:', error);
    throw error;
  }
}

function getSelectedVariantTitle(form) {
  if (!form) {
    return null;
  }
  
  const variantPicker = form.querySelector('variant-picker');
  if (!variantPicker) {
    return null;
  }
  
  const variantScript = variantPicker.querySelector('script[data-variant]');
  if (!variantScript) {
    return null;
  }
  
  const variantData = JSON.parse(variantScript.textContent || '{}');
  return variantData.title || null;
}

function getSelectedVariantData(form) {
  if (!form) {
    return null;
  }

  const variantPicker = form.querySelector('variant-picker');
  if (!variantPicker) {
    return null;
  }

  const variantScript = variantPicker.querySelector('script[data-variant]');
  if (!variantScript) {
    return null;
  }

  try {
    return JSON.parse(variantScript.textContent || '{}');
  } catch (error) {
    return null;
  }
}

function isVariantAllowedForSpecialDate(productTags, specialDateTag, variantTitle) {
  if (!specialDateTag || !productTags || productTags.length === 0) {
    return false;
  }

  // Match any of the special-date tags (same membership as checkProductTagMatch).
  const allowedTags = specialDateTag
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
  if (!allowedTags.length) {
    return false;
  }

  const matchedTag = allowedTags.find((allowedTag) => productTags.includes(allowedTag));
  if (!matchedTag) {
    return false;
  }

  // Empty / missing title = default-title variant — product-level membership is enough.
  // Liquid emits "" for Default Title; requiring a title silently disabled every such variant.
  if (!variantTitle) {
    return true;
  }

  const variantSpecificTag = `${matchedTag}:${variantTitle}`;
  if (productTags.includes(variantSpecificTag)) {
    return true;
  }

  const hasAnyVariantTag = productTags.some((tag) => tag.startsWith(`${matchedTag}:`));
  if (hasAnyVariantTag) {
    return false;
  }

  return true;
}

function parseDateOnly(dateString) {
  if (!dateString) {
    return null;
  }
  // Parse YYYY-MM-DD as local calendar date to avoid UTC off-by-one.
  const datePart = String(dateString).split('T')[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) {
    const fallback = new Date(dateString);
    if (isNaN(fallback.getTime())) {
      return null;
    }
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  date.setHours(0, 0, 0, 0);
  return date;
}

function checkReservedLeadUp(deliveryDate, productTags, variantTitle, days, specialDatesMetaobjects) {
  if (!deliveryDate || !days || days <= 0) {
    return null;
  }

  const deliveryDateObj = parseDateOnly(deliveryDate);
  if (!deliveryDateObj) {
    return null;
  }

  for (const handle in specialDatesMetaobjects) {
    const specialDate = specialDatesMetaobjects[handle];
    if (!specialDate.start_date) {
      continue;
    }
    if (!isVariantAllowedForSpecialDate(productTags, specialDate.tag, variantTitle)) {
      continue;
    }

    const startDate = parseDateOnly(specialDate.start_date);
    if (!startDate) {
      continue;
    }
    const leadStart = new Date(startDate);
    leadStart.setDate(leadStart.getDate() - days);
    leadStart.setHours(0, 0, 0, 0);

    if (deliveryDateObj >= leadStart && deliveryDateObj < startDate) {
      return {tag: specialDate.tag, event: specialDate.event};
    }
  }

  return null;
}

function setButtonLabel(button, text) {
  const label = button.querySelector('.loader-button__text');
  if (label) {
    label.textContent = text;
  } else {
    button.textContent = text;
  }
}

function applyReservedState(button, isReserved) {
  if (!button) {
    return;
  }

  const reservedMessage = window.themeVariables?.strings?.specialCatalogReserved;

  if (isReserved) {
    if (button.dataset.reserveBlocked === undefined) {
      const label = button.querySelector('.loader-button__text');
      button.dataset.reserveBlocked = button.dataset.buttonContent || (label ? label.textContent : button.textContent);
    }
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('button--ternary', 'boa-disabled');
    if (reservedMessage) {
      button.setAttribute('aria-label', reservedMessage);
      setButtonLabel(button, reservedMessage);
    }
  } else if (button.dataset.reserveBlocked !== undefined) {
    setButtonLabel(button, button.dataset.reserveBlocked);
    delete button.dataset.reserveBlocked;
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.classList.remove('button--ternary', 'boa-disabled');
    button.removeAttribute('aria-label');
  }
}

function parseJsonAttribute(jsonString, fallback) {
  try {
    if (jsonString == null || jsonString === '') {
      return fallback;
    }
    // dataset/getAttribute usually decodes entities; handle leftover &quot; just in case.
    const normalized =
      typeof jsonString === 'string' && jsonString.indexOf('&quot;') !== -1
        ? jsonString.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
        : jsonString;
    const parsed = JSON.parse(normalized);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}

function setReservedHidden(el, hidden) {
  if (!el) {
    return;
  }
  if (hidden) {
    if (el.dataset.reserveHidden !== 'true') {
      el.dataset.reserveHidden = 'true';
      el.style.setProperty('display', 'none', 'important');
    }
  } else if (el.dataset.reserveHidden === 'true') {
    delete el.dataset.reserveHidden;
    el.style.removeProperty('display');
  }
}

function getReserveEntry(reserveMap, variantId) {
  if (variantId == null || !reserveMap) {
    return null;
  }
  return reserveMap[variantId] || reserveMap[String(variantId)] || reserveMap[Number(variantId)] || null;
}

function isVariantReserved(deliveryDate, productTags, reserveMap, variantId, specialDatesMetaobjects) {
  const entry = getReserveEntry(reserveMap, variantId);
  if (!entry) {
    return false;
  }
  return Boolean(checkReservedLeadUp(deliveryDate, productTags, entry.title, entry.days, specialDatesMetaobjects));
}

// When the selected variant is reserved (hidden), pick the first visible in-stock option
// so the buy button is usable. Clicking fires variant:change, which re-runs validation.
function selectFirstBuyableOption(scopes) {
  for (const scope of scopes) {
    const nodes = scope.querySelectorAll('.product-form__option-selector [data-variant-id]');
    for (const node of nodes) {
      if (node.dataset.reserveHidden === 'true') {
        continue;
      }
      if (node.classList.contains('variant-card--disabled') || node.classList.contains('is-disabled')) {
        continue;
      }
      if (node.dataset.branchUnavailable === 'true') {
        continue;
      }
      const trigger = node.querySelector('label, input[type="radio"]') || node;
      trigger.click();
      return true;
    }
  }
  return false;
}

function getFormReserveContext(form) {
  const productItem = form.closest('product-item');
  const reserveMap = parseJsonAttribute(
    form.dataset.reserveDays || productItem?.dataset?.reserveDays || '{}',
    {}
  );
  const productTags = parseJsonAttribute(
    form.dataset.productTags || productItem?.dataset?.productTags || '[]',
    []
  );
  return { reserveMap, productTags };
}

const RESERVED_BUY_BUTTON_SELECTOR =
  '[data-product-add-to-cart-button], button[type="submit"]';

function validateReservedVariants(deliveryDate, specialDatesMetaobjects) {
  // No delivery date -> nothing is reserved; restore anything this script hid.
  if (!deliveryDate) {
    document.querySelectorAll('[data-reserve-hidden="true"]').forEach((el) => setReservedHidden(el, false));
    document.querySelectorAll(RESERVED_BUY_BUTTON_SELECTOR).forEach((button) => applyReservedState(button, false));
    return;
  }

  const productForms = document.querySelectorAll('form[is="product-form"]');
  productForms.forEach((form) => {
    const { reserveMap, productTags } = getFormReserveContext(form);

    // Option nodes live in the <variant-picker>, which is linked by form-id and sits
    // OUTSIDE the form element — so scan the form and any picker bound to it.
    // NB: use getAttribute('id') — form.id is shadowed by the <input name="id"> element.
    const formId = form.getAttribute('id');
    const scopes = [form];
    if (formId) {
      document.querySelectorAll(`variant-picker[form-id="${formId}"]`).forEach((picker) => scopes.push(picker));
    }
    scopes.forEach((scope) => {
      scope.querySelectorAll('.product-form__option-selector [data-variant-id]').forEach((node) => {
        const reserved = isVariantReserved(deliveryDate, productTags, reserveMap, node.dataset.variantId, specialDatesMetaobjects);
        setReservedHidden(node, reserved);
      });
    });

    // Disable + relabel ALL buy buttons while the selected variant is reserved
    // (collection cards have both desktop hover + touch icon submit buttons).
    // Keep apply + restore on the same selector so buttons without data-product-add-to-cart-button
    // are not left stuck disabled after the date clears.
    const buttons = form.querySelectorAll(RESERVED_BUY_BUTTON_SELECTOR);
    if (!buttons.length) {
      return;
    }
    const variantData = getSelectedVariantData(form);
    let variantId = variantData?.id;
    if (!variantId) {
      const idInput = form.querySelector('input[name="id"]');
      variantId = idInput ? idInput.value : null;
    }
    const reserved = isVariantReserved(deliveryDate, productTags, reserveMap, variantId, specialDatesMetaobjects);
    // Selected variant reserved but other options buyable -> switch to one (fires variant:change).
    if (reserved && selectFirstBuyableOption(scopes)) {
      return;
    }
    buttons.forEach((button) => applyReservedState(button, reserved));
  });

  // Grid cards: hide when reserve leaves no buyable variant (mirrors boa-product-reserved).
  const productItems = document.querySelectorAll('product-item');
  productItems.forEach((item) => {
    const variants = parseJsonAttribute(item.dataset.variants || '[]', []);
    if (!Array.isArray(variants) || !variants.length) {
      return;
    }
    const reserveMap = parseJsonAttribute(item.dataset.reserveDays || '{}', {});
    const productTags = parseJsonAttribute(item.dataset.productTags || '[]', []);

    let anyReserved = false;
    let hasPurchasable = false;
    for (const variant of variants) {
      if (isVariantReserved(deliveryDate, productTags, reserveMap, variant.id, specialDatesMetaobjects)) {
        anyReserved = true;
      } else if (variant.available) {
        hasPurchasable = true;
      }
      if (anyReserved && hasPurchasable) {
        break;
      }
    }
    setReservedHidden(item, anyReserved && !hasPurchasable);
  });
}

function checkProductTagMatch(productTags, specialDateTag, variantTitle) {
  if (!specialDateTag || !productTags || productTags.length === 0) {
    return false;
  }
  
  const allowedTags = specialDateTag.split(',').map((tag) => {
    return tag.trim();
  });
  const hasMainTag = productTags.some((tag) => {
    return allowedTags.includes(tag);
  });
  
  if (!hasMainTag) {
    return false;
  }
  
  if (!variantTitle) {
    return true;
  }
  
  const mainTag = allowedTags[0];
  const variantSpecificTag = `${mainTag}:${variantTitle}`;
  const hasVariantTag = productTags.includes(variantSpecificTag);
  
  if (hasVariantTag) {
    return true;
  }
  
  return false;
}

function extractCollectionUrl(collectionUrl) {
  if (!collectionUrl) {
    return null;
  }
  
  if (collectionUrl.url) {
    return collectionUrl.url;
  }
  
  return collectionUrl;
}

function updateButtonState(button, isMatch, collectionUrlString) {
  if (isMatch) {
    button.disabled = false;
    button.classList.remove('button--ternary');
    button.removeAttribute('aria-label');
  } else {
    if (collectionUrlString) {
      replaceButtonWithCollectionLink(button, collectionUrlString);
    } else {
      button.disabled = true;
      button.classList.add('button--ternary');
      button.setAttribute('aria-label', 'Product not available for selected delivery date');
    }
  }
}

function replaceButtonWithCollectionLink(button, collectionUrl) {
  if (!button || !collectionUrl) {
    return;
  }
  
  if (button.hasAttribute('data-replaced-with-link')) {
    return;
  }
  
  const allowedAttributes = new Set([
    'aria-label',
    'aria-describedby',
    'aria-labelledby',
    'aria-hidden',
    'aria-expanded',
    'aria-controls',
    'role',
    'tabindex',
    'title',
    'lang',
    'dir',
  ]);
  
  const excludedDataAttributes = new Set([
    'data-product-add-to-cart-button',
  ]);
  
  const buttonAttributes = {};
  const dataAttributes = {};
  
  Array.from(button.attributes).forEach((attribute) => {
    if (attribute.name.startsWith('data-')) {
      if (!excludedDataAttributes.has(attribute.name)) {
        dataAttributes[attribute.name] = attribute.value;
      }
    } else if (allowedAttributes.has(attribute.name)) {
      buttonAttributes[attribute.name] = attribute.value;
    }
  });
  
  const buttonInnerHTML = button.innerHTML;
  
  const linkElement = document.createElement('a');
  linkElement.href = collectionUrl;
  linkElement.className = button.className;
  linkElement.setAttribute('data-replaced-with-link', 'true');
  linkElement.setAttribute('data-original-button-id', button.id || '');
  linkElement.setAttribute('data-original-button-type', button.type || 'submit');
  linkElement.setAttribute('data-original-button-is', button.getAttribute('is') || '');
  
  Object.keys(buttonAttributes).forEach((key) => {
    linkElement.setAttribute(key, buttonAttributes[key]);
  });
  
  Object.keys(dataAttributes).forEach((key) => {
    linkElement.setAttribute(key, dataAttributes[key]);
  });
  
  linkElement.innerHTML = buttonInnerHTML;
 
  const originalButtonData = {
    type: button.type,
    is: button.getAttribute('is'),
    id: button.id,
    classes: Array.from(button.classList),
    attributes: buttonAttributes,
    dataAttributes: dataAttributes,
    innerHTML: buttonInnerHTML,
    ariaLabel: button.getAttribute('aria-label') || '',
  };
  
  linkElement.setAttribute('data-original-button-data', JSON.stringify(originalButtonData));
  
  button.replaceWith(linkElement);
}

function restoreButtonFromCollectionLink(linkElement) {
  if (!linkElement || !linkElement.hasAttribute('data-replaced-with-link')) {
    return;
  }
  
  const originalButtonDataJson = linkElement.getAttribute('data-original-button-data');
  if (!originalButtonDataJson) {
    return;
  }
  
  let originalButtonData;
  try {
    originalButtonData = JSON.parse(originalButtonDataJson);
  } catch (error) {
    return;
  }
  
  if (!originalButtonData) {
    return;
  }
  
  const buttonElement = document.createElement('button');
  buttonElement.type = originalButtonData.type || 'submit';
  buttonElement.id = originalButtonData.id || '';
  buttonElement.className = originalButtonData.classes.join(' ');
  buttonElement.innerHTML = originalButtonData.innerHTML;
  buttonElement.setAttribute('data-product-add-to-cart-button', '');
  
  if (originalButtonData.is) {
    buttonElement.setAttribute('is', originalButtonData.is);
  }
  
  Object.keys(originalButtonData.attributes).forEach((key) => {
    buttonElement.setAttribute(key, originalButtonData.attributes[key]);
  });
  
  Object.keys(originalButtonData.dataAttributes).forEach((key) => {
    buttonElement.setAttribute(key, originalButtonData.dataAttributes[key]);
  });
  
  if (originalButtonData.ariaLabel) {
    buttonElement.setAttribute('aria-label', originalButtonData.ariaLabel);
  }
  
  linkElement.replaceWith(buttonElement);
}

async function getSpecialDateTagAndCollectionUrl(specialDatesMetaobjects) {
  try {
    const cartUrl = `${window.themeVariables?.routes?.cartUrl || '/cart'}.js`;
    const response = await fetch(cartUrl);
    if (!response.ok) {
      return { specialDateTag: null, collectionUrl: null };
    }
    const cart = await response.json();
    const deliveryDate = cart.attributes?.delivery_date;
    const specialDateTag = cart.attributes?.special_date_tag || null;

    if (!deliveryDate) {
      return { specialDateTag: null, collectionUrl: null, deliveryDate: null };
    }

    const specialCatalog = checkSpecialCatalogDate(deliveryDate, specialDatesMetaobjects);
    const collectionUrl = specialCatalog ? specialCatalog.collectionUrl : null;

    return { specialDateTag, collectionUrl, deliveryDate };
  } catch (error) {
    return { specialDateTag: null, collectionUrl: null, deliveryDate: null };
  }
}

async function revalidateReserved(specialDatesMetaobjects) {
  const { specialDateTag, deliveryDate } = await getSpecialDateTagAndCollectionUrl(specialDatesMetaobjects);
  if (!specialDateTag) {
    validateReservedVariants(deliveryDate, specialDatesMetaobjects);
  }
}

async function validateProductForms(specialDateTag = null, collectionUrl = null) {
  const collectionUrlString = extractCollectionUrl(collectionUrl);
  
  if (!specialDateTag) {
    const allReplacedLinks = document.querySelectorAll('[data-replaced-with-link="true"]');
    allReplacedLinks.forEach((linkElement) => {
      restoreButtonFromCollectionLink(linkElement);
    });
    
    const allButtons = document.querySelectorAll('[data-product-add-to-cart-button]');
    allButtons.forEach((button) => {
      updateButtonState(button, true, null);
    });
    return;
  }

  const productForms = document.querySelectorAll('form[is="product-form"]');
  productForms.forEach((form) => {
    const productTagsJson = form.dataset.productTags || '[]';
    let productTags = [];
    try {
      productTags = JSON.parse(productTagsJson);
      if (!Array.isArray(productTags)) {
        productTags = [];
      }
    } catch (error) {
      productTags = [];
    }
    
    const variantTitle = getSelectedVariantTitle(form);
    const isMatch = checkProductTagMatch(productTags, specialDateTag, variantTitle);
    const isVariantAllowed = variantTitle 
      ? isVariantAllowedForSpecialDate(productTags, specialDateTag, variantTitle)
      : true;
    const finalMatch = isMatch && isVariantAllowed;
    
    const button = form.querySelector('[data-product-add-to-cart-button]');
    const replacedLink = form.querySelector('[data-replaced-with-link="true"]');
    
    if (replacedLink) {
      if (finalMatch) {
        restoreButtonFromCollectionLink(replacedLink);
        const restoredButton = form.querySelector('[data-product-add-to-cart-button]');
        if (restoredButton) {
          updateButtonState(restoredButton, true, null);
        }
      } else if (collectionUrlString && replacedLink.href !== collectionUrlString) {
        replacedLink.href = collectionUrlString;
      }
    } else if (button) {
      updateButtonState(button, finalMatch, collectionUrlString);
    }
  });

  const productItems = document.querySelectorAll('product-item');
  productItems.forEach((item) => {
    const productTagsJson = item.dataset.productTags || '[]';
    let productTags = [];
    
    try {
      productTags = JSON.parse(productTagsJson);
      if (!Array.isArray(productTags)) {
        productTags = [];
      }
    } catch (error) {
      const form = item.querySelector('form[is="product-form"]');
      const formTagsJson = form?.dataset?.productTags || '[]';
      try {
        productTags = JSON.parse(formTagsJson);
        if (!Array.isArray(productTags)) {
          productTags = [];
        }
      } catch (nestedError) {
        productTags = [];
      }
    }
    
    const replacedLinks = item.querySelectorAll('[data-replaced-with-link="true"]');
    replacedLinks.forEach((replacedLink) => {
      const isMatch = checkProductTagMatch(productTags, specialDateTag);
      if (isMatch) {
        restoreButtonFromCollectionLink(replacedLink);
        const restoredButton = item.querySelector('[data-product-add-to-cart-button]');
        if (restoredButton) {
          updateButtonState(restoredButton, true, null);
        }
      } else if (collectionUrlString && replacedLink.href !== collectionUrlString) {
        replacedLink.href = collectionUrlString;
      }
    });
    
    const buttons = item.querySelectorAll('[data-product-add-to-cart-button], button[type="submit"]');
    
    buttons.forEach((button) => {
      const isMatch = checkProductTagMatch(productTags, specialDateTag);
      updateButtonState(button, isMatch, collectionUrlString);
    });
  });
}

async function updateSpecialCatalogState(deliveryDate, specialDatesMetaobjects) {
  const specialCatalog = checkSpecialCatalogDate(deliveryDate, specialDatesMetaobjects);

  if (!specialCatalog) {
    await updateCartAttribute({ special_date_tag: '', special_catalog_url: '' });
    return null;
  }
  showSpecialCatalogPopup(specialCatalog, deliveryDate);
  const collectionUrl = extractCollectionUrl(specialCatalog.collectionUrl) || '';
  await updateCartAttribute({ special_date_tag: specialCatalog.tag, special_catalog_url: collectionUrl });
  return specialCatalog;
}

async function handleCartUpdate(event, specialDatesMetaobjects) {
  const cart = event.detail?.cart;
  if (!cart) {
    return;
  }

  const deliveryDate = cart.attributes?.delivery_date;
  if (!deliveryDate) {
    return;
  }

  sessionStorage.removeItem('special-catalog-popup-shown');
  const specialCatalog = await updateSpecialCatalogState(deliveryDate, specialDatesMetaobjects);

  if (!specialCatalog) {
    await validateProductForms();
    validateReservedVariants(deliveryDate, specialDatesMetaobjects);
    return;
  }

  await validateProductForms(specialCatalog.tag, specialCatalog.collectionUrl);
}

/**
 * Reserve check for explicit variant ids (e.g. vase bundle flower+vase).
 * Each entry is either:
 * - { variantId, productTags, reserveMap } — look up days/title in the map, or
 * - { variantId, productTags, days, title } — use days/title directly when no map.
 */
function isVariantsReservedForCart(variants, cart) {
  const deliveryDate = cart?.attributes?.delivery_date;
  if (!deliveryDate || !Array.isArray(variants) || variants.length === 0) {
    return false;
  }

  const specialDatesMetaobjects = initSpecialDates();
  if (!specialDatesMetaobjects || Object.keys(specialDatesMetaobjects).length === 0) {
    return false;
  }

  return variants.some((variant) => {
    if (variant == null || variant.variantId == null || variant.variantId === '') {
      return false;
    }

    const productTags = Array.isArray(variant.productTags) ? variant.productTags : [];

    if (variant.reserveMap) {
      return isVariantReserved(
        deliveryDate,
        productTags,
        variant.reserveMap,
        variant.variantId,
        specialDatesMetaobjects
      );
    }

    const days = Number(variant.days) || 0;
    if (days <= 0) {
      return false;
    }

    return Boolean(
      checkReservedLeadUp(
        deliveryDate,
        productTags,
        variant.title || '',
        days,
        specialDatesMetaobjects
      )
    );
  });
}

// Special-date catalog membership guard for add-to-cart. Unlike the server render
// and button-state gate (which key off the special_date_tag cart attribute, written
// only AFTER a date is picked), this derives the active special date straight from
// delivery_date so it holds during the pick-date-then-add flow where the attribute
// write races the add. Returns true when the product/variant is NOT a catalog member.
function isFormBlockedBySpecialDate(form, cart) {
  if (!form || !cart?.attributes?.delivery_date) {
    return false;
  }

  // Fail OPEN: this runs on the live add-to-cart path (after preventDefault, before the
  // real submit). Any error here must let the sale proceed, never break or wrongly block
  // it — getSelectedVariantTitle does an unguarded JSON.parse of the variant script.
  try {
    const specialDatesMetaobjects = initSpecialDates();
    const specialCatalog = checkSpecialCatalogDate(cart.attributes.delivery_date, specialDatesMetaobjects);
    if (!specialCatalog || !specialCatalog.tag) {
      return false;
    }

    const productTags = parseJsonAttribute(form.dataset.productTags || '[]', []);
    const variantTitle = getSelectedVariantTitle(form);
    const isMatch = checkProductTagMatch(productTags, specialCatalog.tag, variantTitle);
    const isVariantAllowed = variantTitle
      ? isVariantAllowedForSpecialDate(productTags, specialCatalog.tag, variantTitle)
      : true;

    return !(isMatch && isVariantAllowed);
  } catch (error) {
    return false;
  }
}

function isFormReservedForCart(form, cart) {
  if (!form || !cart?.attributes?.delivery_date) {
    return false;
  }

  const { reserveMap, productTags } = getFormReserveContext(form);
  const variantIds = new Set();

  const idInput = form.querySelector('input[name="id"]');
  if (idInput?.value != null && idInput.value !== '') {
    variantIds.add(idInput.value);
  }

  form.querySelectorAll('input[name="items[][id]"]').forEach((input) => {
    if (input.value) {
      variantIds.add(input.value);
    }
  });

  if (!variantIds.size) {
    return false;
  }

  return isVariantsReservedForCart(
    Array.from(variantIds).map((variantId) => ({
      variantId,
      productTags,
      reserveMap,
    })),
    cart
  );
}

// Sitewide API for cart interceptor / bundle paths — keeps reserve logic in one place.
window.BoaCatalogPopup = {
  isFormReservedForCart,
  isVariantsReservedForCart,
  isFormBlockedBySpecialDate,
  getFormReserveContext,
  handleBranchSelected: null,
};

async function initSpecialCatalogPopup() {
  const specialDatesMetaobjects = initSpecialDates();
  window.boaCatalogPopupHandleBranchSelected = async (event) => await handleCartUpdate(event, specialDatesMetaobjects);
  window.BoaCatalogPopup.handleBranchSelected = window.boaCatalogPopupHandleBranchSelected;

  // Quick-buy / quick-view drawers lazy-load their product-form after open, with no event
  // fired — re-run reserved validation whenever a product-form is injected into the DOM.
  let revalidateTimer;
  const reservedObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) {
          continue;
        }
        if (node.matches?.('form[is="product-form"]') || node.querySelector?.('form[is="product-form"]')) {
          clearTimeout(revalidateTimer);
          revalidateTimer = setTimeout(() => revalidateReserved(specialDatesMetaobjects), 150);
          return;
        }
      }
    }
  });
  reservedObserver.observe(document.body, { childList: true, subtree: true });
  
  document.documentElement.addEventListener('variant:change', async (event) => {
    const form = event.target?.closest('form[is="product-form"]');
    if (!form) {
      return;
    }
    
    const specialDatesMetaobjects = initSpecialDates();
    try {
      const { specialDateTag, collectionUrl, deliveryDate } = await getSpecialDateTagAndCollectionUrl(specialDatesMetaobjects);

      if (!specialDateTag) {
        validateReservedVariants(deliveryDate, specialDatesMetaobjects);
        return;
      }
      
      const productTagsJson = form.dataset.productTags || '[]';
      let productTags = [];
      
      productTags = JSON.parse(productTagsJson);
      
      const variantTitle = getSelectedVariantTitle(form);
      const isMatch = checkProductTagMatch(productTags, specialDateTag, variantTitle);
      const isVariantAllowed = variantTitle 
        ? isVariantAllowedForSpecialDate(productTags, specialDateTag, variantTitle)
        : true;
      
      const button = form.querySelector('[data-product-add-to-cart-button]');
      if (button) {
        const collectionUrlString = extractCollectionUrl(collectionUrl);
        updateButtonState(button, isMatch && isVariantAllowed, collectionUrlString);
      }
    } catch (error) {
      return;
    }
  });

  const popup = document.getElementById('special-catalog-popup');
  const catalogButton = popup?.querySelector('#go-to-catalog-btn');

  catalogButton?.addEventListener('click', function() {
    const catalogUrl = this.getAttribute('data-catalog-url');
    if (catalogUrl) {
      window.location.href = catalogUrl;
    }
  });

  try {
    const cartUrl = `${window.themeVariables?.routes?.cartUrl || '/cart'}.js`;
    const response = await fetch(cartUrl);
    
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    
    const cart = await response.json();
    const deliveryDate = cart.attributes?.delivery_date;
    
    if (!deliveryDate) {
      return;
    }
    const specialCatalog = await updateSpecialCatalogState(deliveryDate, specialDatesMetaobjects);

    if (!specialCatalog) {
      await validateProductForms();
      validateReservedVariants(deliveryDate, specialDatesMetaobjects);
      return;
    }

    await validateProductForms(specialCatalog.tag, specialCatalog.collectionUrl);

    } catch (error) {
      console.error('Error fetching initial cart state:', error);
    }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSpecialCatalogPopup);
} else {
  initSpecialCatalogPopup();
}

document.addEventListener('product:rerender', async () => {
  const specialDatesMetaobjects = initSpecialDates();
  const { specialDateTag, collectionUrl, deliveryDate } = await getSpecialDateTagAndCollectionUrl(specialDatesMetaobjects);
  await validateProductForms(specialDateTag, collectionUrl);
  if (!specialDateTag) {
    validateReservedVariants(deliveryDate, specialDatesMetaobjects);
  }
});